import React, { useEffect } from 'react';
import styled from "@emotion/styled";
import { RootState, i18n, mq, setFlowType, setIsComparisonFunnel, setProgressBarPercentage, useAppDispatch, useAppSelector } from 'src/common';
import { Card, HtmlText, HtmlTextLink, Text } from 'src/components';
import { CustomerPolicyComparisonForm, allCustomerPolicyComparisonFormItems } from 'src/components/forms/customerPolicyComparison.form';
import { useParams } from 'react-router-dom';

const CardStyle = styled(Card)(
  mq({
    padding: ['16px', '16px', "24px", "24px", "40px"],
    marginBottom: 16
  }))

const TitleStyle = styled(Text)({
  marginBottom: 4
})

const DescriptionStyle = styled(Text)(
  mq({
    marginBottom: [44, 44, 32, 32, 32, 32, 32]
  })
)

const FormContainer = styled.div(
  mq({
    marginBottom: [44, 44, 32, 32, 32, 32, 32]
  })
)

const htmlTextLinks: Array<HtmlTextLink> = [
  { value: 'form', link: 'https://kompare.hr/obrazac-za-prituzbe/', translation: i18n.t('pages.customerPage.htmlTextLinkTranslations.form') },
  { value: 'mail', link: 'mailto:szop@kompare.hr', translation: 'szop@kompare.hr' },
  { value: 'here', link: 'https://kompare.hr/informacije-o-koristenju-osobnih-podataka/', translation: i18n.t('pages.customerPage.htmlTextLinkTranslations.here') },
  { value: 'privacyPolicy', link: 'https://kompare.hr/informacije-o-koristenju-osobnih-podataka/', translation: i18n.t('pages.customerPage.htmlTextLinkTranslations.privacyPolicy') },
  { value: 'terms', link: 'https://kompare.hr/uvjeti-koristenja/', translation: i18n.t('pages.customerPage.htmlTextLinkTranslations.terms') },
];

type ComparisonParams = {
  birthDate?: string;
  insurancePrice?: string;
  hasBList?: boolean;
};

// Parse the optional URL segment, e.g. "birthDate=1988-04-16&insurancePrice=150&hasBList=no".
// Only keys actually present in the URL are set, so spreading the result never
// overwrites the form's defaults with undefined.
const parseComparisonParams = (raw?: string): ComparisonParams => {
  if (!raw) return {};
  const params = new URLSearchParams(raw);
  const parsed: ComparisonParams = {};
  const birthDate = params.get('birthDate');
  const insurancePrice = params.get('insurancePrice');
  const hasBList = params.get('hasBList');
  if (birthDate) parsed.birthDate = birthDate;
  if (insurancePrice) parsed.insurancePrice = insurancePrice;
  if (hasBList !== null) parsed.hasBList = ['yes', 'da', 'true', '1'].includes(hasBList.toLowerCase());
  return parsed;
};

const CustomerPolicyComparisonPage = () => {
  const { comparisonParams } = useParams();
  const parsedParams = parseComparisonParams(comparisonParams);

  const dispatch = useAppDispatch();
  const birthDate = useAppSelector((store: RootState) => store.model.customer.data.birthDate);

  // URL param takes precedence, store birthDate is the fallback.
  const resolvedBirthDate = parsedParams.birthDate ?? birthDate;

  // Hide the fields already provided via the URL — their values are still passed
  // to the form through `customer`, so they get submitted without being shown.
  // Exception: when birthDate is the only URL param, show the full form with
  // birthDate prefilled (but still editable) instead of hiding it.
  const prefilledKeys = Object.keys(parsedParams);
  const onlyBirthDateFromUrl = prefilledKeys.length === 1 && prefilledKeys[0] === 'birthDate';
  const prefilledFromUrl = onlyBirthDateFromUrl ? new Set<string>() : new Set(prefilledKeys);
  const formItems = allCustomerPolicyComparisonFormItems.filter((item) => !prefilledFromUrl.has(item));

  useEffect(() => {
    dispatch(setProgressBarPercentage(20));
    dispatch(setFlowType('web'));
    dispatch(setIsComparisonFunnel(true));
  }, []);

  return (
    <>
      <CardStyle title={<TitleStyle type="large" bold>{i18n.t('pages.customerPolicyComparisonPage.title')}</TitleStyle>}>
        <DescriptionStyle type='small'>{i18n.t('pages.customerPolicyComparisonPage.description')}</DescriptionStyle>
        <FormContainer>
          <CustomerPolicyComparisonForm
            customer={{ ...parsedParams, birthDate: resolvedBirthDate }}
            formItems={formItems}
          />
        </FormContainer>
        <HtmlText i18nKey="pages.customerPolicyComparisonPage.gdprConsentWithParams" links={htmlTextLinks} />
      </CardStyle>
    </>
  );
}

export default CustomerPolicyComparisonPage;